import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import { useStrictLocalization } from "@faire/web--source/common/localization";

import { SelfServeNodes } from "../../../types";
import { addOrEditItems } from "../../instantAnswers/orders-addOrEditItems";
import { cancelOrder } from "../../instantAnswers/orders-cancelOrder";
import { combineOrders } from "../../instantAnswers/orders-combineOrders";
import { editOrderInformation } from "../../instantAnswers/orders-editOrderInformation";
import { RequestCommissionChanges } from "../../instantAnswers/orders-requestCommissionChanges";
import { splitOrder } from "../../instantAnswers/orders-splitOrder";
import { vatIssueIA } from "../../instantAnswers/orders-vatIssue";
import { orderUnfulfilledStateVisibilityFn } from "../../visibilityFunctions/orderStateVisibilityFn";

export const useOrderUnfulfilledBrandNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const articles = useArticles();
  return {
    AddOrEditItemsUnfulfilledState: {
      id: "AddOrEditItemsUnfulfilledState",
      label: strictLocalize({
        defaultMessage: "Add or edit items in order",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderUnfulfilledStateVisibilityFn,
      instantAnswer: addOrEditItems(strictLocalize),
      articles: [articles.FulfillingOrders.EDIT_RETAILER_ORDER],
    },
    EditOrderInformationUnfulfilledState: {
      id: "EditOrderInformationUnfulfilledState",
      label: strictLocalize({
        defaultMessage: "Edit order information",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderUnfulfilledStateVisibilityFn,
      instantAnswer: editOrderInformation(strictLocalize),
      articles: [articles.FulfillingOrders.EDIT_RETAILER_ORDER],
    },
    VatIssueUnfulfilledState: {
      id: "VatIssueUnfulfilledState",
      label: strictLocalize({
        defaultMessage: "VAT issue",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      instantAnswer: vatIssueIA(strictLocalize),
      articles: [
        articles.International.VAT_WORK_EU_ORDERS,
        articles.International.PAY_VAT_NON_EU,
        articles.International.VAT_WORK_NON_EU_ORDERS,
      ],
      isNodeVisible: orderUnfulfilledStateVisibilityFn,
    },
    SplitOrderUnfulfilledState: {
      id: "SplitOrderUnfulfilledState",
      label: strictLocalize({
        defaultMessage: "Split order",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      instantAnswer: splitOrder(strictLocalize),
      articles: [
        articles.FulfillingOrders.SPLIT_ATS_ORDER,
        articles.FulfillingOrders.SPLIT_PREORDER,
      ],
      isNodeVisible: orderUnfulfilledStateVisibilityFn,
    },
    CombineOrdersUnfulfilledState: {
      id: "CombineOrdersUnfulfilledState",
      label: strictLocalize({
        defaultMessage: "Combine orders",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      instantAnswer: combineOrders(strictLocalize),
      articles: [articles.FulfillingOrders.EDIT_RETAILER_ORDER],
      isNodeVisible: orderUnfulfilledStateVisibilityFn,
    },
    RequestCommissionChangesUnfulfilledState: {
      id: "RequestCommissionChangesUnfulfilledState",
      label: strictLocalize({
        defaultMessage: "Request commission change",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderUnfulfilledStateVisibilityFn,
      instantAnswer: RequestCommissionChanges(strictLocalize),
      articles: [articles.FaireDirect.ZERO_COMMISSION],
    },
    CancelOrderUnfulfilledState: {
      id: "CancelOrderUnfulfilledState",
      label: strictLocalize({
        defaultMessage: "Cancel order",
        description: {
          text: "A node in the Orders section of the full page self-serve experience of Faire's Help Center",
        },
      }),
      level: 2,
      isNodeVisible: orderUnfulfilledStateVisibilityFn,
      instantAnswer: cancelOrder(strictLocalize),
      articles: [articles.FulfillingOrders.FULFILLING_ORDER],
    },
  };
};
